import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { CreateFolderDialogProps } from '../AssetExplorer.types';
import { useAssetExplorerApi } from '../AssetExplorerConfig';

export const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
    open,
    parentId,
    onClose,
    onCreateComplete,
}) => {
    const { createFolder } = useAssetExplorerApi();
    const [folderName, setFolderName] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreate = async () => {
        if (!folderName.trim()) {
            setError('Folder name is required');
            return;
        }

        // Validate folder name
        if (!/^[a-zA-Z0-9_\- ]+$/.test(folderName.trim())) {
            setError('Folder name can only contain letters, numbers, spaces, hyphens and underscores');
            return;
        }

        setCreating(true);
        setError(null);

        try {
            await createFolder({
                name: folderName.trim(),
                parentId: parentId || undefined,
            });

            setFolderName('');
            onCreateComplete();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create folder');
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        if (creating) return;
        setFolderName('');
        setError(null);
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !creating && folderName.trim()) {
            handleCreate();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreateNewFolderIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Create New Folder
                </Typography>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <TextField
                    autoFocus
                    label="Folder Name"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    disabled={creating}
                    color="secondary"
                    placeholder="Enter folder name..."
                    helperText="Allowed characters: letters, numbers, spaces, hyphens, and underscores"
                    sx={{ mt: 1 }}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={creating}
                    variant="outlined"
                    color="secondary"
                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCreate}
                    disabled={creating || !folderName.trim()}
                    startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <CreateNewFolderIcon />}
                >
                    {creating ? 'Creating...' : 'Create Folder'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateFolderDialog;
